import "./OrderCard.scss";
import { MotifIcon, MotifTooltip, MotifTruncate } from "@ey-xd/motif-react";
import { IconoirNavArrowUp, IconoirNavArrowDown } from "@ey-xd/motif-icon";
import { useState, useEffect } from "react";
import { ProductAccordian } from "../../../Checkout/Components/OrderExpected/ProductAccordian";
import OrderItemsHeaderTooltip from "../../../../components/OrderPipeline/OrderItemsHeaderTooltip/OrderItemsHeaderTooltip";

import { getLocalDateStringShortMonth } from "../../../../utils/dateFunction";
import {
  OrderExtensionExistingProps,
  SkuDetailsExistingOrderType,
} from "../../type";
import { SKU } from "../../../../store/slices/CheckoutSlice";

interface OrderCardProps {
  orderExistingData?: OrderExtensionExistingProps | null;
  engagementData: {
    EngagementId: string;
    EngagementName: string;
  }[];
}

export const OrderCard = ({
  orderExistingData,
  engagementData,
}: OrderCardProps) => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);

    // Listen for window resize events
    window.addEventListener("resize", handleResize);

    // Clean up the listener when the component unmounts
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isOrderExpanded, setIsOrderExpanded] = useState(false);
  const engArray: { EngagementId: string; EngagementName: string }[] =
    engagementData || [];

  const getSkuDetailsForOrderItems = (
    skuDetails: SkuDetailsExistingOrderType[] = [],
  ): SKU[] => {
    return skuDetails.map((sku) => ({
      SkuId: sku.SkuId,
      ProductId: sku.ProductId,
      ProductSkuNum: 0,
      Name: sku.SkuName,
      Description: sku.SkuDescription,
      CostMacroCategoryCd: sku.CostMacroCategoryCd || "",
      CostMetricCd: sku.CostMetricCd,
      CostMetricCost: sku.CostMetricCost,
      CurrencyCd: sku.CurrencyCd,
      CostIntervalCd: sku.CostIntervalCd,
      CostIntervalMin: sku.CostIntervalMin,
      CostIntervalMax: sku.CostIntervalMax,
      IsAutoProvisioned: sku.IsAutoProvisioned,
      IsReturnable: sku.IsReturnable,
      StatusCd: sku.SkuStatusCd,
      CreatedUser: sku.SkuCreatedUser,
      UpdatedUser: sku.SkuUpdatedUser,
      CreatedDtm: sku.SkuCreatedDate,
      UpdatedDtm: sku.SkuUpdatedDate,
      OrderQuantity: sku.OrderSkuQuantity ?? sku.OrderSkuQunatity ?? 0,
      OrderInterval: sku.OrderSkuIntervals ?? 0,
      SkuType: sku.SkuType,
      SkuRelation: (sku.SkuRelation || []).map((relation) => ({
        SkuId: relation.SkuId,
        ProductId: relation.ProductId,
        ProductSkuNum: 0,
        Name: relation.SkuName,
        Description: relation.SkuDescription,
        CostMacroCategoryCd: relation.CostMacroCategoryCd || "",
        CostMetricCd: relation.CostMetricCd,
        CostMetricCost: relation.CostMetricCost,
        CurrencyCd: relation.CurrencyCd,
        CostIntervalCd: relation.CostIntervalCd,
        CostIntervalMin: relation.CostIntervalMin,
        CostIntervalMax: relation.CostIntervalMax,
        IsAutoProvisioned: relation.IsAutoProvisioned,
        IsReturnable: relation.IsReturnable,
        StatusCd: relation.SkuStatusCd,
        CreatedUser: relation.SkuCreatedUser,
        UpdatedUser: relation.SkuUpdatedUser,
        CreatedDtm: relation.SkuCreatedDate,
        UpdatedDtm: relation.SkuUpdatedDate,
        OrderQuantity:
          relation.OrderSkuQuantity ?? relation.OrderSkuQunatity ?? 0,
        OrderInterval: relation.OrderSkuIntervals ?? 0,
        SkuType: relation.SkuType,
        SkuRelation: [],
      })),
    }));
  };

  const renderEng = (
    engArray: { EngagementId: string; EngagementName: string }[],
  ) => {
    if (engArray.length === 0) {
      return <div className="heading">-</div>;
    } else if (engArray.length === 1) {
      return (
        <>
          <div className="heading">{engArray[0].EngagementId}</div>
          <div className="sub-heading">{engArray[0].EngagementName}</div>
        </>
      );
    } else {
      return (
        <div className="eng-grid">
          <div>
            <div className="heading">{engArray[0].EngagementId}</div>
            <div className="sub-heading">
              <MotifTruncate
                maxLines={1}
                maxWidth={100}
                className="card-title"
                tooltipProps={{
                  placement: "top",
                }}
              >
                {engArray[0].EngagementName}
              </MotifTruncate>
            </div>
          </div>
          <div className="show-exp-eng">
            <div className="exp-eng-item">
              <MotifTooltip
                contentClassName="tooltip-content"
                allowHover
                allowClick={true}
                id={`tooltip`}
                placement="top"
                offset={[0, 18]}
                flip
                trigger={<span aria-label="Tooltip">+1</span>}
                usePortal
                variant="default"
              >
                <>
                  <div className="heading">{engArray[1].EngagementId}</div>
                  <div className="sub-heading">
                    {engArray[1].EngagementName}
                  </div>
                </>
              </MotifTooltip>
            </div>
          </div>
        </div>
      );
    }
  };
  const totalCost = orderExistingData?.TotalCost || "-";
  const totalCostFormatted = totalCost.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  return (
    <>
      <div className="order-card-existing-wrapper">
        <div
          className={`order-card extension-order ${
            isOrderExpanded ? "expanded" : ""
          }`}
        >
          <div className="flex-item">
            <div className="heading">
              {orderExistingData?.OrderDescription || "-"}
            </div>
            <div className="sub-heading">
              {orderExistingData?.OrderId || "-"}
            </div>
            <div
              onClick={() => setIsOrderExpanded(!isOrderExpanded)}
              className="view-details-link"
            >
              <div>
                {isOrderExpanded ? "Close order details" : "View order details"}
              </div>
              <span className="icon-grid">
                {isOrderExpanded ? (
                  <MotifIcon iconFunction={IconoirNavArrowUp} size="16" />
                ) : (
                  <MotifIcon iconFunction={IconoirNavArrowDown} size="16" />
                )}
              </span>
            </div>
          </div>
          <div className="flex-item">{renderEng(engArray)}</div>
          <div className="flex-item">
            <div className="heading">
              {getLocalDateStringShortMonth(
                orderExistingData?.OrderDate || "",
              ) || "-"}
            </div>
            <div className="sub-heading">Req. date</div>
          </div>
          <div className="flex-item">
            <div className="heading">
              <span className="order-total">{totalCostFormatted}</span>
            </div>
            <div className="sub-heading">Total cost</div>
          </div>
        </div>
        {isOrderExpanded && (
          <div className="order-items-list">
            <div className="product-list order-success-content">
              <div className="product-list-content">
                <table className="motif-table-standard">
                  <thead>
                    <tr>
                      <th className="sku-col">
                        <OrderItemsHeaderTooltip
                          label="SKU name/SKU ID"
                          type="sku"
                        />
                      </th>
                      <th className="text-right-align unit-cost-col">
                        <OrderItemsHeaderTooltip
                          label="Unit cost"
                          type="unitCost"
                          maxWidth={
                            screenWidth < 1630
                              ? (screenWidth - 200) * 0.083
                              : undefined
                          }
                        />
                      </th>
                      <th className="units-per-period-col">
                        <OrderItemsHeaderTooltip
                          label="Units per period"
                          type="unitsPerPeriod"
                          maxWidth={
                            screenWidth < 1630
                              ? (screenWidth - 200) * 0.051
                              : undefined
                          }
                        />
                      </th>
                      <th className="periods-col">
                        <OrderItemsHeaderTooltip
                          label="No. of periods"
                          type="numberOfPeriods"
                          maxWidth={
                            screenWidth < 1630
                              ? (screenWidth - 200) * 0.054
                              : undefined
                          }
                        />
                      </th>
                      <th className="text-right-align subtotal-col">
                        <OrderItemsHeaderTooltip
                          label="Subtotal"
                          type="subtotal"
                          maxWidth={
                            screenWidth < 1630
                              ? (screenWidth - 200) * 0.068
                              : undefined
                          }
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(orderExistingData?.Details || []).map((detail) => (
                      <ProductAccordian
                        key={detail.ProductId || detail.ProductName}
                        productName={detail.ProductName}
                        skuDetails={getSkuDetailsForOrderItems(
                          detail.SkuDetails,
                        )}
                        showEngagementColumn={false}
                        isOrderpipelineOrderTab={true}
                        showQuantityColumn={false}
                        isCheckoutPage={true}
                        screenWidth={screenWidth}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
